// Mapping-Editor fuer den Kaufland-Lister-Detail-Pane (Stufe 4 — KL-005/006/007/078/081/082).
//
// UI-Polish 2026-05-21: card-basiertes layout im vario-design-system. Sticky-Header mit
// progress + save/reset. Field-groups als collapsible cards mit progress-meta. Pro feld
// ein flex-row mit linker meta-spalte (label + key + desc + required-marker) + source-
// dropdown + value-input. Visueller status-stripe links zeigt mapped/required/template-
// status. Vario-CSS-variables statt hardcoded hex.
//
// Konsumiert die drei Stufe-3-Edge-Functions:
//   - kaufland-listing-schema   → Felder pro field-group + allowedValues + Pflicht-Flags
//   - vario-mapping-sources     → gruppierte Vario-Quellen-Liste fuer das Source-Dropdown
//   - kaufland_get_mapping_profile / _save_mapping_profile  → RPCs aus Migration 0077
//
// Seit 2026-08-06 ist dieser Editor die EINZIGE Ansicht fuer Feldwerte. Der frueher parallel
// existierende Reiter "Optionale Felder" zeigte nach dem Umbau auf das Mapping-Modell genau
// dieselben Zeilen wie die Gruppe "Optionale Felder" hier und ist deshalb entfernt. Sein
// einziger eigener Inhalt, der Mindestpreis, ist als Direktwert-Zeile hierher gewandert
// (renderMinPriceRow) — er haengt an kaufland.optional_field_values/_overrides, weil der
// Preis-Push ihn per SQL liest und keine Vario-Quelle aufloesen koennte.

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, openModal } from '@vario-connect/frontend-design';
import { wireTwoStepConfirm } from '../shared/listing-detail-pane.js';
import { localeForStorefront as localeFor } from '../../lib/storefront-locale.js';
import { initialeMappings } from '../../lib/mapping-defaults.js';
import {
  callFn,
  cssEscape,
  dedupFieldsByKey,
  escapeHtml,
  fieldStatus,
  isDirectValueOnlyField,
  darfGepflegtWerden,
  isMapped,
  isVarioField,
  parseOverrideValue,
  renderFieldRow,
  renderHtmlPreview,
  sourceSelectValue,
  CREATE_EAV_VALUE,
  CLEAR_VALUE,
  FIELD_ROW_STYLES,
  GROUP_PRIORITY,
  type DisplayField,
  type ExistingFieldMapping,
  type SchemaField,
  type SourceDef,
  type SourcesResp,
} from '../shared/mapping-field-row.js';

// ---------- Domain ----------

export interface MappingTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  /** Optional callback fuer den Vorschau-Button im Header. Parent tab schaltet auf preview-pane. */
  onPreviewRequested?: () => void;
}

export interface MappingTabListing {
  storefront: string;
  vario_category_id: string | null;
  kaufland_category_id: number | null;
  vario_listing_id: string;
  vario_article_id: string;
}

interface SchemaResp {
  fieldGroups: Array<{ key: string; label: string; description?: string }>;
  fields: SchemaField[];
  meta: {
    cache: { categoryAttributes: { count: number; latestFetchedAt: string | null }; refreshPending: boolean };
    vatIndicatorsCount: number;
    shippingGroupsCount: number;
  };
}

interface ProfileResp {
  profile: { id: string; storefront: string; locale: string; category_id: number } | null;
  fields: ExistingFieldMapping[];
  variants: unknown[];
}

/** Schluessel des Mindestpreises im Direktwert-Pfad (kaufland.optional_field_values). */
const MIN_PRICE_ATTR = 'base:minimum_price';

/** Zeile aus kaufland_list_optional_field_overrides. Gelesen wird davon nur der Mindestpreis. */
interface MinPriceRow {
  attribute_id: string;
  default_value: unknown;
  override_value: unknown;
}

/**
 * Mindestpreis-Zeile im Editor.
 *
 * Zwei getrennte Werte mit zwei getrennten RPCs, deshalb auch zwei getrennte Dirty-Flags: der
 * geteilte Wert gilt fuer alle Artikel der Storefront (`kaufland.optional_field_values`), der
 * Artikelwert nur fuer dieses Listing (`kaufland.optional_field_overrides`). Beide werden erst
 * beim Speichern geschrieben, wie die Mappings — anders als die Artikelwerte der uebrigen Felder,
 * die eine eigene Sofort-RPC haben (siehe saveOverride).
 */
interface MinPriceState {
  shared: unknown;
  article: unknown;
  dirtyShared: boolean;
  dirtyArticle: boolean;
}

interface EditorState {
  schema: SchemaResp;
  sources: SourcesResp;
  profile: ProfileResp;
  /** Deduplizierte + nach Pflicht sortierte Felder fuer das Rendering. */
  displayFields: DisplayField[];
  mappings: Map<string, ExistingFieldMapping>;
  /** Listing-spezifische Feld-Overrides (Migration 0129): kaufland_field_key -> fester Wert.
   *  Gesetzt = sticht die Profil-Quelle nur fuer dieses Listing. */
  overrides: Map<string, unknown>;
  minPrice: MinPriceState;
  dirty: boolean;
}

// Locale-Ableitung (localeForStorefront) liegt seit 2026-08-16 in lib/storefront-locale.ts —
// eine Quelle fuer alle vier Reiter, weil der Wert Teil des Mapping-Profil-Schluessels ist und
// jede Kopie driften kann.

// ---------- Component-scoped CSS ----------

// Brand-Farben + neutrale Layer kommen aus dem GO-Brand-Layer (bridge.css).
// Hier nur Lister-Editor-spezifische Klassen.
const COMPONENT_STYLES = `
  .kl-map-shell { display: flex; flex-direction: column; gap: 14px; padding: 16px; padding-bottom: 0; }

  /* ── Header: Titel/Subtitle + Action-Buttons + Progress-Bar ──────────────── */
  .kl-map-header {
    background: var(--v-surface, #fff);
    padding: 14px 16px;
    border: 1px solid #E4E7EC;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  }
  .kl-map-title { font-size: 16px; font-weight: 600; color: #101828; }
  .kl-map-subtitle { font-size: 12px; color: #475467; margin-top: 2px; }

  .kl-map-progress-row {
    display: flex; align-items: center; gap: 10px;
    margin-top: 12px;
  }
  .kl-map-progress-bar {
    flex: 1 1 auto;
    height: 8px;
    background: #E4E7EC;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
  }
  .kl-map-progress-seg-ok { background: #15803D; transition: width 220ms ease-out; }
  .kl-map-progress-seg-warn { background: var(--go-orange, #1A1D21); transition: width 220ms ease-out; }
  .kl-map-progress-text {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px;
    color: #475467;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  /* ── Quick-Filter Toolbar ───────────────────────────────────────────────── */
  .kl-map-quickbar {
    display: flex; align-items: center; gap: 10px;
    margin-top: 10px;
  }
  .kl-map-quickbar .kl-map-search {
    flex: 1 1 auto;
    position: relative;
    display: flex; align-items: center;
    background: #fff;
    border: 1px solid #D0D5DD;
    border-radius: 6px;
    height: 32px;
    padding: 0 10px;
    gap: 6px;
  }
  .kl-map-search > .v-icon { color: #98A2B3; font-size: 12px; }
  .kl-map-search > input {
    flex: 1 1 auto;
    border: 0; outline: none; background: transparent;
    height: 100%; font: inherit; font-size: 12.5px; color: #101828;
  }
  .kl-map-toggle {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 12px;
    background: #fff; border: 1px solid #D0D5DD;
    border-radius: 999px;
    font-size: 12px; color: #475467; cursor: pointer;
    transition: background 140ms ease, color 140ms ease, border-color 140ms ease;
  }
  .kl-map-toggle:hover { border-color: #98A2B3; }
  .kl-map-toggle[data-active="1"] {
    background: rgba(26,29,33,0.08);
    border-color: var(--go-orange, #1A1D21);
    color: var(--go-orange, #1A1D21);
  }
  .kl-map-toggle .v-icon { font-size: 11px; }
  .kl-map-automap-btn {
    flex: 0 0 auto;
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 12px;
    background: #fff; border: 1px solid #D0D5DD;
    border-radius: 6px;
    font-size: 12px; color: #344054; cursor: pointer; font-family: inherit;
    transition: border-color 140ms ease, background 140ms ease;
  }
  .kl-map-automap-btn:hover { border-color: #98A2B3; background: #F9FAFB; }
  .kl-map-automap-btn[disabled] { opacity: 0.55; cursor: not-allowed; }
  .kl-map-automap-btn .v-icon { font-size: 12px; color: var(--go-orange, #1A1D21); }

  /* ── Action-Buttons (Header) Button-Hierarchie ──────────────────────────── */
  .kl-map-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .kl-map-btn {
    display: inline-flex; align-items: center; gap: 6px;
    height: 32px; padding: 0 14px;
    border-radius: 6px; cursor: pointer; font: inherit; font-size: 12.5px;
    transition: filter 140ms ease, background 140ms ease, border-color 140ms ease;
  }
  .kl-map-btn--primary {
    background: linear-gradient(180deg, #1A1D21 0%, #0D0F11 100%);
    color: #fff; border: 1px solid #0D0F11;
    box-shadow: 0 1px 2px rgba(26,29,33,0.20);
  }
  .kl-map-btn--primary:hover { filter: brightness(0.96); }
  .kl-map-btn--primary[disabled] { opacity: 0.55; cursor: not-allowed; filter: none; }
  .kl-map-btn--secondary {
    background: #fff; color: #344054; border: 1px solid #D0D5DD;
  }
  .kl-map-btn--secondary:hover { border-color: #98A2B3; background: #F9FAFB; }
  .kl-map-btn--ghost {
    background: transparent; color: #475467; border: 1px solid transparent;
  }
  .kl-map-btn--ghost:hover { border-color: #D0D5DD; background: #F9FAFB; }
  .kl-map-btn--icon {
    width: 32px; height: 32px; padding: 0; justify-content: center;
    background: transparent; color: #475467; border: 1px solid transparent;
  }
  .kl-map-btn--icon:hover { border-color: #D0D5DD; background: #F9FAFB; }
  .kl-map-save-dirty::after {
    content: '*';
    margin-left: 2px;
    font-weight: 700;
  }

  /* ── Section (Pflichtfelder / Optionale Felder) ─────────────────────────── */
  .kl-map-group {
    border: 1px solid #E4E7EC;
    border-radius: 10px;
    background: var(--v-surface, #fff);
    overflow: hidden;
  }
  .kl-map-group > summary {
    list-style: none;
    cursor: pointer;
    padding: 12px 14px;
    background: #F9FAFB;
    display: flex; align-items: center; gap: 10px;
    border-bottom: 1px solid transparent;
    transition: border-color 160ms ease-out;
    user-select: none;
  }
  .kl-map-group[open] > summary { border-bottom-color: #E4E7EC; }
  .kl-map-group > summary::-webkit-details-marker { display: none; }
  .kl-map-group-chevron {
    transition: transform 220ms ease-out;
    font-size: 12px; color: #667085; flex-shrink: 0;
  }
  .kl-map-group[open] > summary .kl-map-group-chevron { transform: rotate(90deg); }
  .kl-map-group-title { font-weight: 600; font-size: 13.5px; flex: 1; color: #101828; }
  .kl-map-group-meta {
    font-size: 11.5px; color: #475467;
    display: flex; gap: 10px; align-items: center; flex-shrink: 0;
  }
  .kl-map-group-mini-progress {
    width: 60px; height: 4px;
    background: #E4E7EC; border-radius: 2px; overflow: hidden;
    display: flex; flex-shrink: 0;
  }
  .kl-map-group-mini-progress > div { transition: width 220ms ease-out; }
  .kl-map-group-mini-count {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px; color: #475467; white-space: nowrap;
  }

  .kl-map-group-body { padding: 0; }
  .kl-map-group-body > .kl-map-field + .kl-map-field { border-top: 1px solid #F2F4F7; }

  /* ── Field-Row ───────────────────────────────────────────────────────────── */
  /* Das kl-map-field-Markup selbst (Grid, Status-Streifen, Label, Dropdown, Value-Input,
     Override-Spalte) ist in FIELD_ROW_STYLES (shared/mapping-field-row.ts) ausgelagert, direkt
     bei dem Code, der es erzeugt. Hier bleibt nur, was zum Editor-Layout gehoert (Gruppen-Rahmen
     oben, Mindestpreis-Zeile unten). */

  /* ── Mindestpreis-Zeile (Direktwert, kein Schema-Feld) ──────────────────── */
  /* box-sizing explizit: es gibt bewusst keinen globalen border-box-Reset, und width:100% plus
     padding wuerde sonst aus der Spalte laufen. */
  .kl-map-minprice .kl-map-field-source input {
    width: 100%; height: 36px; padding: 0 12px; box-sizing: border-box;
    border: 1px solid #D0D5DD; border-radius: 6px; background: #fff;
    font: inherit; font-size: 13px; color: #101828;
  }
  .kl-map-minprice .kl-map-field-source input:focus {
    outline: 0; border-color: var(--go-orange, #1A1D21);
    box-shadow: 0 0 0 3px rgba(26,29,33,0.18);
  }
  .kl-map-badge {
    font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;
    color: #3538CD; background: #EEF4FF; border-radius: 4px; padding: 1px 5px;
  }

  /* ── Action-Bar (Speichern/Reset) ───────────────────────────────────────── */
  /* Kein position:sticky — die Leiste soll im normalen Fluss am Ende des
     Mapping-Editors bleiben und mit dem Inhalt scrollen, nicht am Rand kleben. */
  .kl-map-sticky-actions {
    background: #fff;
    border-top: 1px solid #E4E7EC;
    padding: 14px 16px;
    margin: 0 -16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
`;

// ---------- Public entrypoint ----------

export async function renderMappingTab(
  host: HTMLElement,
  ctx: MappingTabCtx,
  listing: MappingTabListing,
): Promise<void> {
  host.innerHTML = `
    <style>${COMPONENT_STYLES}${FIELD_ROW_STYLES}</style>
    <div class="v-pa-md" style="display:flex;flex-direction:column;align-items:center;gap:0.5em;color:var(--v-color-text-muted,#666);">
      <span class="v-icon fa-regular fa-spinner-third fa-spin" style="font-size:1.5em;"></span>
      <span>Lade Schema und Quellen …</span>
    </div>
  `;

  const categoryId = listing.kaufland_category_id;
  if (!categoryId) {
    host.innerHTML = `
      <style>${COMPONENT_STYLES}${FIELD_ROW_STYLES}</style>
      <div class="v-pa-md">
        <div class="v-alert v-alert--info">
          <strong>Mapping noch nicht möglich.</strong><br>
          Dieses Listing hat noch keine Kaufland-Kategorie. Bitte zuerst im Vario-Listing-Slot
          (Verkaufskanäle-Reiter am Artikel) eine Kategorie zuweisen. Danach stehen hier auch die
          optionalen Felder und der Mindestpreis.
        </div>
      </div>`;
    return;
  }

  const storefront = listing.storefront;
  const locale = localeFor(storefront);

  const [schemaResp, sourcesResp, profileResp, overridesResp, minPriceResp] = await Promise.all([
    callFn<SchemaResp>(ctx, 'kaufland-listing-schema', { storefront, locale, categoryId }),
    callFn<SourcesResp>(ctx, 'vario-mapping-sources', {
      action: 'list',
      storefront,
      articleId: listing.vario_article_id,
      listingId: listing.vario_listing_id,
    }),
    ctx.supabase.rpc('kaufland_get_mapping_profile', { _storefront: storefront, _locale: locale, _category_id: categoryId }),
    // Listing-spezifische Overrides (Migration 0129) — fail-soft, blockiert den Editor nicht.
    ctx.supabase.rpc('kaufland_list_listing_field_overrides', { _storefront: storefront, _vario_article_id: listing.vario_article_id }),
    // Mindestpreis-Direktwerte — fail-soft. Faellt die Ladung aus, steht die Zeile leer da; ein
    // Speichern schreibt dann nur, was der Nutzer selbst eingetippt hat (dirty-Flags), ein leer
    // gebliebenes Feld loescht also nichts.
    ctx.supabase.rpc('kaufland_list_optional_field_overrides', { _vario_article_id: listing.vario_article_id }),
  ]);

  if (!schemaResp.ok) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Schema-Ladung fehlgeschlagen: ${escapeHtml(schemaResp.error)}</div>`;
    return;
  }
  if (!sourcesResp.ok) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Quellen-Ladung fehlgeschlagen: ${escapeHtml(sourcesResp.error)}</div>`;
    return;
  }
  if (profileResp.error) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Profil-Ladung fehlgeschlagen: ${escapeHtml(profileResp.error.message)}</div>`;
    return;
  }

  const schema = schemaResp.value;
  const sources = sourcesResp.value;
  const profileRaw = (profileResp.data ?? { profile: null, fields: [], variants: [] }) as ProfileResp;
  const profile: ProfileResp = {
    profile: profileRaw.profile ?? null,
    fields: Array.isArray(profileRaw.fields) ? profileRaw.fields : [],
    variants: Array.isArray(profileRaw.variants) ? profileRaw.variants : [],
  };

  // Dedup nach `key`, dann Pflicht nach oben sortieren (innerhalb Gruppen-Prioritaet).
  // Nur der Mindestpreis wird entfernt: er bekommt keine Kategorie-Zuordnung und wird weiter
  // unten als eigene Direktwert-Zeile gerendert (renderMinPriceRow).
  const displayFields = dedupFieldsByKey(schema.fields)
    .filter((f) => !isDirectValueOnlyField(f.key))
    // Felder, die Kaufland zwar im Schema fuehrt, von Verkaeufern aber nicht annimmt.
    // Begruendung je Feld am Helfer.
    .filter((f) => darfGepflegtWerden(f));
  // ANGEBOTS-FELDER BLEIBEN IM EDITOR (Ruecknahme vom 2026-08-24).
  //
  // Sie waren kurzzeitig herausgefiltert, mit der Begruendung, eine Zuordnung dort werde
  // nie gelesen -- `buildProductDataPayload` ueberspringt `field_group='offer'`
  // (kaufland-product-data/src/index.ts:995). Das galt nur fuer die PRODUKTDATEN. Das
  // ANGEBOT baut ein anderer Verbraucher, und der liest genau diese Zeilen:
  // supabase/functions/kaufland-listing-preview/index.ts:1085
  //   `if (m.field_group === 'offer') { unit[m.kaufland_field_key] = value; }`
  //
  // Ohne sie meldete die Vorschau fuer jedes Listing REQUIRED_EMPTY auf offer.condition,
  // offer.listing_price, offer.amount, offer.handling_time und offer.vat_indicator und
  // blockierte das Aufschalten. Ein Verbraucher geprueft, auf alle geschlossen -- der
  // Fehler, den dieser Kommentar verhindern soll.
  //
  // Was WO belegt werden darf, entscheidet weiterhin die Feldregel-Tabelle
  // (kaufland-listing-schema/feldregeln.ts): `amount` und `vat_indicator` nehmen keinen
  // festen Wert an, die uebrigen schon.
  displayFields.sort((a, b) => {
    if (a.required !== b.required) return a.required ? -1 : 1;
    const aP = GROUP_PRIORITY[a.originalFieldGroup] ?? 99;
    const bP = GROUP_PRIORITY[b.originalFieldGroup] ?? 99;
    return aP - bP;
  });

  // Gespeicherte Zeilen, sonst Einrichtungs-Vorschlaege -- Letztere aber NUR, solange noch
  // kein Profil gespeichert ist. Sobald eines existiert, heisst eine fehlende Zeile
  // „ausdruecklich nicht gemappt", nicht „noch nie eingerichtet". Vorher legte der Editor
  // dort wieder einen Vorschlag an, und ein auf „nicht gemappt" gestelltes Feld mit
  // Werteliste (etwa `material`) stand nach dem Speichern erneut auf „Fester Wert".
  // Begruendung und Messung in `lib/mapping-defaults.ts`.
  const mappings = initialeMappings(displayFields, profile.fields, profile.profile !== null);

  // Listing-Overrides in eine Map kaufland_field_key -> Wert (Migration 0129). Fail-soft.
  const overrides = new Map<string, unknown>();
  if (overridesResp.error) {
    console.warn('[kaufland-list-mapping] overrides load failed:', overridesResp.error.message);
  } else {
    for (const row of (overridesResp.data as Array<{ kaufland_field_key: string; value: unknown }> | null) ?? []) {
      overrides.set(row.kaufland_field_key, row.value);
    }
  }

  // Mindestpreis-Direktwerte. Fail-soft wie die Overrides oben.
  const minRows = (minPriceResp.error ? [] : (minPriceResp.data ?? [])) as MinPriceRow[];
  if (minPriceResp.error) {
    console.warn('[kaufland-list-mapping] Mindestpreis-Ladung fehlgeschlagen:', minPriceResp.error.message);
  }
  const minRow = minRows.find((r) => r.attribute_id === MIN_PRICE_ATTR) ?? null;
  const minPrice: MinPriceState = {
    shared: minRow?.default_value ?? null,
    article: minRow?.override_value ?? null,
    dirtyShared: false,
    dirtyArticle: false,
  };

  const state: EditorState = { schema, sources, profile, displayFields, mappings, overrides, minPrice, dirty: false };
  renderEditor(host, ctx, listing, state);
}

// ---------- Renderer ----------

function renderEditor(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): void {
  const mappedCount = countMapped(state);
  const requiredOpen = countRequiredOpen(state);
  const totalFields = state.displayFields.length;
  const profileExists = state.profile.profile !== null;

  // Vario-Felder werden VOR der Pflicht/Optional-Aufteilung extrahiert — ein Feld
  // soll in genau einer Gruppe erscheinen, sonst doppelter Editor + doppelte
  // mini-progress-Zaehlung. Pflichtflag bleibt erhalten (Stripe + Star-Marker im
  // FieldRow zeigen weiterhin Pflicht-Status innerhalb der Vario-Gruppe).
  const varioFields = state.displayFields.filter((f) => isVarioField(f));
  const remainingFields = state.displayFields.filter((f) => !isVarioField(f));
  const requiredFields = remainingFields.filter((f) => f.required);
  const optionalFields = remainingFields.filter((f) => !f.required);

  const refreshPendingNote = state.schema.meta.cache.refreshPending
    ? renderAlert('info', 'Kaufland-Schema-Cache wird aktualisiert (Hintergrund-Job). Beim nächsten Reload sind die Felder frisch.')
    : '';
  const sgEmpty = state.schema.meta.shippingGroupsCount === 0
    ? renderAlert('info', 'Keine Versandgruppen im Cache - bitte vorher in den Einstellungen „Versandgruppen aktualisieren".')
    : '';

  const requiredTotal = requiredFields.length;
  const requiredMapped = requiredFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  const optionalTotal = optionalFields.length;
  const optionalMapped = optionalFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  const varioTotal = varioFields.length;
  const varioMapped = varioFields.filter((f) => isMapped(state.mappings.get(f.key))).length;
  // Vario-Article-URL ist der einzige verfuegbare "External-Link" auf dieser Ebene —
  // Kaufland-Backend-URL wuerde kaufland_unit_id brauchen, die wir hier (Mapping-Editor)
  // nicht im Context haben. Fallback: disabled-Icon-Button.
  const externalHref = state.sources.meta?.varioBaseUrl
    ? `${state.sources.meta.varioBaseUrl}/products/articles/${encodeURIComponent(listing.vario_article_id)}/detail`
    : null;

  host.innerHTML = `
    <style>${COMPONENT_STYLES}${FIELD_ROW_STYLES}</style>
    <div class="kl-map-shell">

      <div class="kl-map-header">
        <div style="display:flex;align-items:flex-start;gap:12px;">
          <div style="flex:1;min-width:0;">
            <div class="kl-map-title">Mapping-Editor</div>
            <div class="kl-map-subtitle">
              Storefront <strong>${escapeHtml(listing.storefront.toUpperCase())}</strong>
              · Kategorie <strong>${listing.kaufland_category_id}</strong>
              · ${state.schema.meta.cache.categoryAttributes.count} Kategorie-Attribute
              · ${state.schema.meta.vatIndicatorsCount} VAT-Indikatoren
              · ${state.schema.meta.shippingGroupsCount} Versandgruppen
              ${profileExists ? '· <span style="color:#15803D;">gespeichert</span>' : '· <span style="color:#475467;">neu</span>'}
            </div>
          </div>
          <div class="kl-map-actions">
            ${externalHref
              ? `<a href="${escapeHtml(externalHref)}" target="_blank" rel="noopener" class="kl-map-btn kl-map-btn--icon" title="Vario-Artikel öffnen"><span class="v-icon fa-light fa-arrow-up-right-from-square"></span></a>`
              : `<button type="button" class="kl-map-btn kl-map-btn--icon" disabled title="Externer Link nicht verfügbar"><span class="v-icon fa-light fa-arrow-up-right-from-square"></span></button>`}
            <button type="button" class="kl-map-btn kl-map-btn--ghost" data-action="reset" title="Änderungen verwerfen und Profil neu laden">
              <span class="v-icon fa-light fa-rotate-left"></span>Zurücksetzen
            </button>
            <button type="button" class="kl-map-btn kl-map-btn--secondary" data-action="preview" id="kl-map-preview" title="Push-Vorschau anzeigen - bei ungespeicherten Änderungen wird vorher automatisch gespeichert">
              <span class="v-icon fa-light fa-eye"></span>Vorschau
            </button>
            <button type="button" class="kl-map-btn kl-map-btn--primary" data-action="save" id="kl-map-save">
              <span class="v-icon fa-light fa-floppy-disk"></span>Speichern
            </button>
          </div>
        </div>

        ${renderProgressBar(mappedCount, totalFields, requiredOpen)}

        <div class="kl-map-quickbar">
          <div class="kl-map-search">
            <span class="v-icon fa-light fa-magnifying-glass"></span>
            <input type="search" id="kl-map-search" placeholder="Feldname suchen…" autocomplete="off" />
          </div>
          <button type="button" class="kl-map-toggle" id="kl-map-toggle-required" title="Nur Pflichtfelder zeigen, die noch nicht gemappt sind">
            <span class="v-icon fa-light fa-circle"></span>
            Nur Pflicht offen
          </button>
          <button type="button" class="kl-map-automap-btn" id="kl-map-automap" title="Felder mit passendem Quellnamen automatisch zuordnen">
            <span class="v-icon fa-light fa-wand-magic-sparkles"></span>
            Auto-Map Vorschläge
          </button>
        </div>

        ${refreshPendingNote ? `<div style="margin-top:10px;">${refreshPendingNote}</div>` : ''}
        ${sgEmpty ? `<div style="margin-top:10px;">${sgEmpty}</div>` : ''}
      </div>

      <div id="kl-map-groups" style="display:flex;flex-direction:column;gap:12px;">
        ${renderDisplayGroup({ key: 'required', label: 'Pflichtfelder', description: 'Alle Pflichtangaben für Kaufland - dedupliziert' }, requiredFields, state, true, requiredMapped, requiredTotal)}
        ${renderDisplayGroup({ key: 'vario', label: 'Vario Felder', description: 'Werte, die typischerweise in Vario am Artikel gepflegt werden' }, varioFields, state, true, varioMapped, varioTotal)}
        <!-- Optionale Felder aufgeklappt: bis 2026-08-06 standen sie in einem eigenen Reiter und
             waren dort direkt sichtbar. Zugeklappt waeren sie nach dem Zusammenlegen schwerer zu
             finden als vorher — und der Mindestpreis am Ende der Gruppe waere unsichtbar. -->
        ${renderDisplayGroup({ key: 'optional', label: 'Optionale Felder', description: 'Sinnvoll, aber nicht erzwungen' }, optionalFields, state, true, optionalMapped, optionalTotal, renderMinPriceRow(state))}
      </div>

      <div class="kl-map-sticky-actions">
        <button type="button" class="kl-map-btn kl-map-btn--ghost" data-action="reset-bottom" title="Änderungen verwerfen">
          <span class="v-icon fa-light fa-rotate-left"></span>Zurücksetzen
        </button>
        <button type="button" class="kl-map-btn kl-map-btn--secondary" data-action="preview-bottom" title="Push-Vorschau">
          <span class="v-icon fa-light fa-eye"></span>Vorschau
        </button>
        <button type="button" class="kl-map-btn kl-map-btn--primary" data-action="save-bottom" id="kl-map-save-bottom">
          <span class="v-icon fa-light fa-floppy-disk"></span>Speichern
        </button>
      </div>

    </div>
  `;

  wireEvents(host, ctx, listing, state);
}

/** Horizontale 3-Segment-Progressbar. Gruen = gemappt, orange = Pflicht offen, rest grau. */
function renderProgressBar(mapped: number, total: number, requiredOpen: number): string {
  if (total === 0) return '';
  const okPct = (mapped / total) * 100;
  const warnPct = (requiredOpen / total) * 100;
  return `
    <div class="kl-map-progress-row">
      <div class="kl-map-progress-bar" role="progressbar" aria-valuenow="${mapped}" aria-valuemin="0" aria-valuemax="${total}">
        <div class="kl-map-progress-seg-ok" style="width: ${okPct}%;"></div>
        <div class="kl-map-progress-seg-warn" style="width: ${warnPct}%;"></div>
      </div>
      <div class="kl-map-progress-text">${mapped} / ${total} Felder gemappt${requiredOpen > 0 ? ` · ${requiredOpen} Pflicht offen` : ''}</div>
    </div>
  `;
}

/**
 * Eine aufklappbare Feldgruppe.
 *
 * `extraRows` haengt fertiges Zeilen-Markup hinter die Felder der Gruppe — genutzt fuer die
 * Mindestpreis-Zeile, die kein Schema-Feld ist und deshalb nicht in `fields` steckt. Sie zaehlt
 * bewusst NICHT in `mapped`/`total`: die Zaehler beziehen sich auf Kategorie-Zuordnungen, der
 * Mindestpreis ist ein Direktwert und hat keine.
 */
function renderDisplayGroup(
  g: { key: string; label: string; description?: string },
  fields: DisplayField[],
  state: EditorState,
  defaultOpen: boolean,
  mapped: number,
  total: number,
  extraRows = '',
): string {
  // Leere Gruppe weglassen — aber nicht, wenn Zusatzzeilen da sind: sonst verschwindet die
  // Mindestpreis-Zeile bei einer Kategorie ohne optionale Attribute.
  if (fields.length === 0 && extraRows === '') return '';
  const reqOpen = fields.filter((f) => f.required && !isMapped(state.mappings.get(f.key))).length;

  return `
    <details class="kl-map-group" data-group-key="${escapeHtml(g.key)}" ${defaultOpen ? 'open' : ''}>
      <summary>
        <span class="v-icon fa-light fa-chevron-right kl-map-group-chevron"></span>
        <span class="kl-map-group-title">${escapeHtml(g.label)}</span>
        <span class="kl-map-group-meta">
          ${renderMiniProgress(mapped, total, reqOpen)}
          <span class="kl-map-group-mini-count">${mapped} / ${total}</span>
        </span>
      </summary>
      <div class="kl-map-group-body">
        ${fields.map((f) => renderFieldRow(f, {
          sources: state.sources,
          mapping: state.mappings.get(f.key),
          override: state.overrides.get(f.key),
        }, { withCreateEav: true })).join('')}
        ${extraRows}
      </div>
    </details>
  `;
}

/**
 * Mindestpreis als Direktwert-Zeile.
 *
 * Kein Quellen-Dropdown: der Preis-Push (apps/worker/src/jobs/kaufland-push-prices.ts) liest den
 * Wert per SQL aus kaufland.optional_field_values/_overrides und koennte eine Vario-Quelle nicht
 * aufloesen. Deshalb steht in der Mitte, wo sonst das Dropdown sitzt, direkt das Eingabefeld fuer
 * den storefront-weiten Wert, und rechts wie ueberall der Wert nur fuer diesen Artikel.
 *
 * Das Badge "Direktwert" ist nicht Deko: ohne den Hinweis wirkt die Zeile wie ein Feld, dem das
 * Dropdown fehlt.
 *
 * Nicht zu verwechseln mit dem Mindestpreis im Aufschalten-Formular (listing-detail-pane.ts) —
 * der landet in `snapshot.offerOverrides` und gilt nur fuer die einmalige Aufschaltung.
 */
export function renderMinPriceRow(state: EditorState): string {
  const txt = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  const sharedTxt = txt(state.minPrice.shared);
  const articleTxt = txt(state.minPrice.article);
  const articleSet = articleTxt !== '';
  const status = sharedTxt !== '' || articleSet ? 'fixed' : 'unmapped';

  return `
    <div class="kl-map-field kl-map-minprice"
         data-status="${status}"
         data-field-key="${escapeHtml(MIN_PRICE_ATTR)}"
         data-search-label="mindestpreis ${escapeHtml(MIN_PRICE_ATTR)}"
         data-required="0">
      <div class="kl-map-field-meta">
        <div class="kl-map-field-label-row">
          <span class="kl-map-field-label" title="Feld-Code: ${escapeHtml(MIN_PRICE_ATTR)}">Mindestpreis (Cents)</span>
          <span class="kl-map-badge" title="Wird vom Preis-Push direkt gelesen und kann keiner Vario-Quelle folgen">Direktwert</span>
        </div>
        <div class="kl-map-field-typeline">${escapeHtml(MIN_PRICE_ATTR)} · currency_cents</div>
        <div class="kl-map-field-desc">
          Untergrenze für den laufenden Preis-Push. Leer = kein Mindestpreis.
        </div>
      </div>
      <div class="kl-map-field-source">
        <input type="number" data-min-price="shared" value="${escapeHtml(sharedTxt)}" placeholder="—" />
        <div class="kl-map-field-value-cap">alle Artikel dieser Storefront</div>
      </div>
      <div class="kl-map-field-value-shell">
        <div class="kl-map-field-override" title="Eigener Mindestpreis nur für diesen Artikel - sticht den storefront-weiten Wert. Leer = erbt ihn.">
          <input type="number" class="kl-map-field-override-input${articleSet ? ' is-set' : ''}"
                 data-min-price="article" value="${escapeHtml(articleTxt)}" placeholder="erbt links" />
          <div class="kl-map-field-override-cap${articleSet ? ' is-set' : ''}">${articleSet ? '✓ nur dieser Artikel' : 'nur dieser Artikel'}</div>
        </div>
      </div>
    </div>
  `;
}

function renderMiniProgress(mapped: number, total: number, requiredOpen: number): string {
  if (total === 0) return '<div class="kl-map-group-mini-progress"></div>';
  const okPct = (mapped / total) * 100;
  const warnPct = (requiredOpen / total) * 100;
  return `
    <div class="kl-map-group-mini-progress">
      <div style="background:#15803D;width:${okPct}%;"></div>
      <div style="background:var(--go-orange,#1A1D21);width:${warnPct}%;"></div>
    </div>
  `;
}

/** Kaufland-Feldtyp → vorgeschlagener Vario-EAV-Typ.
 *
 *  Vario kennt BOOLEAN, INTEGER, TEXT, DATE, DATE_TIME, JSON, OBJECT, ARRAY, CURRENCY, DECIMAL,
 *  MEMO, SELECT_SINGLE, SELECT_MULTI (`common-eav-EavAttribute.type`). Entscheidend ist der
 *  Unterschied zwischen `TEXT` (einzeilig) und `MEMO` (Langtext) — Kauflands Beschreibungsfelder
 *  brauchen MEMO, sonst schneidet Vario die Eingabe optisch auf eine Zeile.
 *
 *  Der Vorschlag ist im Dialog änderbar: Wer bewusst ein einzeiliges Feld für eine Kurz-
 *  beschreibung will, soll das können. */
export function suggestEavType(dataType: string): string {
  switch (dataType) {
    case 'text': return 'MEMO';
    case 'short_text':
    case 'small_text':
    case 'tiny_text':
    case 'string':
    case 'ean':
    case 'url':
    case 'image_url':
    case 'company': return 'TEXT';
    case 'integer': return 'INTEGER';
    case 'number':
    case 'float':
    case 'si_quantity': return 'DECIMAL';
    case 'currency_cents': return 'CURRENCY';
    case 'bool': return 'BOOLEAN';
    case 'date': return 'DATE';
    case 'enum': return 'SELECT_SINGLE';
    case 'multi_enum': return 'SELECT_MULTI';
    default: return 'TEXT';
  }
}

const EAV_TYPE_LABELS: Array<{ value: string; label: string }> = [
  { value: 'TEXT', label: 'Text (einzeilig)' },
  { value: 'MEMO', label: 'Langtext (mehrzeilig)' },
  { value: 'INTEGER', label: 'Ganzzahl' },
  { value: 'DECIMAL', label: 'Dezimalzahl' },
  { value: 'CURRENCY', label: 'Betrag' },
  { value: 'BOOLEAN', label: 'Ja / Nein' },
  { value: 'DATE', label: 'Datum' },
  { value: 'DATE_TIME', label: 'Datum + Uhrzeit' },
  { value: 'SELECT_SINGLE', label: 'Auswahl (eine)' },
  { value: 'SELECT_MULTI', label: 'Auswahl (mehrere)' },
];

function renderAlert(level: 'info' | 'warn' | 'error', message: string): string {
  const cls = level === 'error' ? 'v-alert--error' : level === 'warn' ? 'v-alert--warn' : 'v-alert--info';
  return `<div class="v-alert ${cls}" style="font-size:0.85em;padding:0.5em 0.8em;">${escapeHtml(message)}</div>`;
}

// ---------- State helpers ----------

/**
 * Roh-Eingabe des Mindestpreises → jsonb-taugliche Zahl (null = ungesetzt).
 *
 * Als Zahl, nicht als Text: so lag der Wert schon vor dem Umzug aus dem Reiter "Optionale Felder"
 * in der Tabelle, und der Preis-Push vergleicht ihn numerisch. Eine nicht parsbare Eingabe bleibt
 * Rohtext statt NaN, damit der Wert nicht kaputt gespeichert wird — dasselbe Muster wie
 * parseOverrideValue in mapping-field-row.ts.
 */
export function parseMinPriceValue(raw: string): unknown {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : t;
}

function countMapped(state: EditorState): number {
  let n = 0;
  for (const f of state.displayFields) if (isMapped(state.mappings.get(f.key))) n += 1;
  return n;
}

function countRequiredOpen(state: EditorState): number {
  let n = 0;
  for (const f of state.displayFields) if (f.required && !isMapped(state.mappings.get(f.key))) n += 1;
  return n;
}

// renderHtmlPreview liegt in shared/mapping-field-row.ts (siehe dort): das iframe-Markup entsteht
// in renderValueInput, also gehoert die Funktion, die es fuellt, in dieselbe Datei.

// ---------- Event wiring ----------

function wireEvents(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): void {
  const groupsHost = host.querySelector<HTMLElement>('#kl-map-groups')!;

  groupsHost.querySelectorAll<HTMLSelectElement>('select[data-field-source]').forEach((sel) => {
    sel.addEventListener('change', () => onSourceChange(host, ctx, listing, state, sel.dataset['fieldSource']!, sel.value));
  });
  groupsHost.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field-value]').forEach((inp) => {
    // Select-elemente feuern `change`, text-inputs/textarea `input`. Beide binden — bei
    // text-inputs ist `change` zusaetzlich (feuert beim blur), bei selects ist `input`
    // nicht zuverlaessig. Wir rufen onValueChange in beiden faellen; ist idempotent.
    const handler = () => onValueChange(host, state, inp.dataset['fieldValue']!, (inp as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value);
    inp.addEventListener('input', handler);
    inp.addEventListener('change', handler);
  });

  // Zeichenzähler + HTML-Vorschau der Langtext-Direktwerte. Delegiert, damit die Listener ein
  // `rerenderRow` überleben.
  groupsHost.addEventListener('input', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.matches !== 'function' || !t.matches('textarea[data-field-value]')) return;
    const ta = t as HTMLTextAreaElement;
    const key = ta.dataset['fieldValue'] ?? '';
    const zaehler = groupsHost.querySelector<HTMLElement>(`[data-charcount-for="${cssEscape(key)}"]`);
    if (zaehler) {
      const limit = state.displayFields.find((f) => f.key === key)?.maxLength ?? null;
      zaehler.textContent = `${ta.value.length}${limit ? ` / ${limit}` : ''} Zeichen`;
      if (limit) zaehler.classList.toggle('is-over', ta.value.length > limit);
    }
    const vorschau = groupsHost.querySelector<HTMLIFrameElement>(`iframe[data-html-preview-of="${cssEscape(key)}"]`);
    if (vorschau && !vorschau.hidden) renderHtmlPreview(vorschau, ta.value);
  });
  groupsHost.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.matches !== 'function' || !t.matches('[data-html-preview-for]')) return;
    const key = t.dataset['htmlPreviewFor'] ?? '';
    const vorschau = groupsHost.querySelector<HTMLIFrameElement>(`iframe[data-html-preview-of="${cssEscape(key)}"]`);
    const ta = groupsHost.querySelector<HTMLTextAreaElement>(`textarea[data-field-value="${cssEscape(key)}"]`);
    if (!vorschau || !ta) return;
    vorschau.hidden = !vorschau.hidden;
    t.textContent = vorschau.hidden ? 'HTML-Vorschau' : 'Vorschau ausblenden';
    if (!vorschau.hidden) renderHtmlPreview(vorschau, ta.value);
  });

  // Listing-Override-Inputs (Migration 0129): delegiert auf groupsHost, damit der Listener
  // auch nach rerenderRow (Quellen-Wechsel ersetzt eine Row) erhalten bleibt. Nur `change`
  // (nicht `input`) — ein RPC pro Tastendruck waere zu viel; change feuert bei blur/commit.
  groupsHost.addEventListener('change', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.matches !== 'function' || !t.matches('[data-field-override]')) return;
    const el = t as HTMLInputElement | HTMLSelectElement;
    void saveOverride(ctx, listing, state, el.dataset['fieldOverride']!, el.value, el);
  });

  // Mindestpreis: nur Zustand merken und als ungespeichert markieren. Geschrieben wird erst beim
  // Speichern (saveMinPrice in saveProfile) — die uebrigen Artikelwerte haben ihre eigene
  // Sofort-RPC, der Mindestpreis laeuft mit dem Sammel-Save.
  groupsHost.addEventListener('change', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || typeof t.matches !== 'function' || !t.matches('[data-min-price]')) return;
    const el = t as HTMLInputElement;
    const wert = parseMinPriceValue(el.value);
    if (el.dataset['minPrice'] === 'article') {
      state.minPrice.article = wert;
      state.minPrice.dirtyArticle = true;
      const gesetzt = wert !== null;
      el.classList.toggle('is-set', gesetzt);
      const cap = el.parentElement?.querySelector('.kl-map-field-override-cap');
      if (cap) {
        cap.classList.toggle('is-set', gesetzt);
        cap.textContent = gesetzt ? '✓ nur dieser Artikel' : 'nur dieser Artikel';
      }
    } else {
      state.minPrice.shared = wert;
      state.minPrice.dirtyShared = true;
    }
    state.dirty = true;
    updateSaveButton(host, state);
  });

  host.querySelector<HTMLButtonElement>('[data-action="save"]')!.addEventListener('click', () => void saveProfile(host, ctx, listing, state));
  host.querySelector<HTMLButtonElement>('[data-action="reset"]')!.addEventListener('click', () => void renderMappingTab(host, ctx, listing));
  host.querySelector<HTMLButtonElement>('[data-action="preview"]')!.addEventListener('click', () => void requestPreview(host, ctx, listing, state));
  // Sticky-Bar-Buttons spiegeln die Header-Aktionen (gleicher Handler)
  host.querySelector<HTMLButtonElement>('[data-action="save-bottom"]')?.addEventListener('click', () => void saveProfile(host, ctx, listing, state));
  host.querySelector<HTMLButtonElement>('[data-action="reset-bottom"]')?.addEventListener('click', () => void renderMappingTab(host, ctx, listing));
  host.querySelector<HTMLButtonElement>('[data-action="preview-bottom"]')?.addEventListener('click', () => void requestPreview(host, ctx, listing, state));

  // Quick-Filter: Live-Such-Filter
  const searchInput = host.querySelector<HTMLInputElement>('#kl-map-search');
  searchInput?.addEventListener('input', () => applyQuickFilter(host, searchInput.value, isOnlyRequiredOpenActive(host)));
  // Quick-Filter: Toggle "Nur Pflicht offen"
  const toggleBtn = host.querySelector<HTMLButtonElement>('#kl-map-toggle-required');
  toggleBtn?.addEventListener('click', () => {
    const active = toggleBtn.dataset['active'] === '1';
    toggleBtn.dataset['active'] = active ? '0' : '1';
    applyQuickFilter(host, searchInput?.value ?? '', !active);
  });
  // Auto-Map-Vorschläge — zweistufige Bestätigung (Projektregel: keine window.confirm)
  const autoMapBtn = host.querySelector<HTMLButtonElement>('#kl-map-automap');
  if (autoMapBtn) {
    wireTwoStepConfirm(
      autoMapBtn,
      `<span class="v-icon fa-regular fa-wand-magic-sparkles"></span> Vorschläge wirklich anwenden?`,
      () => void runAutoMap(host, state),
    );
  }

  updateSaveButton(host, state);
}

function isOnlyRequiredOpenActive(host: HTMLElement): boolean {
  return host.querySelector<HTMLButtonElement>('#kl-map-toggle-required')?.dataset['active'] === '1';
}

/** Show/hide field rows based on search term + "only required open" toggle.
 *  Reine DOM-Operation, keine state-Mutation — Filter ist nur Anzeige-Filter. */
function applyQuickFilter(host: HTMLElement, searchTerm: string, onlyRequiredOpen: boolean): void {
  const term = searchTerm.trim().toLowerCase();
  host.querySelectorAll<HTMLElement>('.kl-map-field').forEach((row) => {
    const label = row.dataset['searchLabel'] ?? '';
    const required = row.dataset['required'] === '1';
    const status = row.dataset['status'] ?? '';
    let visible = true;
    if (term && !label.includes(term)) visible = false;
    if (onlyRequiredOpen && !(required && status === 'required-empty')) visible = false;
    row.hidden = !visible;
  });
}

/** Auto-Map: fuer jedes nicht-gemappte Field versuchen, eine Source zu finden, deren
 *  letztes Pfad-Segment case-insensitive zum Field-Code matched (z.B. field 'ean' →
 *  source 'article.ean'). Vor dem Anwenden: zweistufiger Klick am Button. Toast danach. */
function runAutoMap(host: HTMLElement, state: EditorState): void {
  const candidates: Array<{ fieldKey: string; sourceKey: string }> = [];
  for (const f of state.displayFields) {
    if (isMapped(state.mappings.get(f.key))) continue;
    const target = f.key.toLowerCase();
    let found: string | null = null;
    outer: for (const group of state.sources.groups) {
      for (const s of group.sources) {
        const lastSeg = s.sourceKey.split('.').pop()?.toLowerCase() ?? '';
        if (lastSeg === target) { found = s.sourceKey; break outer; }
      }
    }
    if (found) candidates.push({ fieldKey: f.key, sourceKey: found });
  }
  if (candidates.length === 0) {
    showToast('Keine Auto-Map-Vorschläge gefunden.', 'info');
    return;
  }

  // Mappings anwenden ohne re-render — onSourceChange erledigt das pro Field.
  for (const c of candidates) {
    const sel = host.querySelector<HTMLSelectElement>(`select[data-field-source="${cssEscape(c.fieldKey)}"]`);
    if (!sel) continue;
    sel.value = c.sourceKey;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
  showToast(`${candidates.length} Felder automatisch gemappt`, 'success');
}

async function requestPreview(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState): Promise<void> {
  // Vorschau zeigt den gespeicherten profil-stand. Bei dirty zuerst speichern, sonst sieht
  // der user veralteten kram. Save-failure stoppt den tab-switch — der user soll erst den
  // fehler beheben.
  if (state.dirty) {
    const ok = await saveProfile(host, ctx, listing, state, { silent: true });
    if (!ok) return;
  }
  if (ctx.onPreviewRequested) ctx.onPreviewRequested();
  else showToast('Kein Vorschau-Handler verfuegbar.', 'error');
}

function onSourceChange(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState, fieldKey: string, newSourceKey: string): void {
  const displayField = state.displayFields.find((f) => f.key === fieldKey);
  if (!displayField) return;
  const fg = displayField.originalFieldGroup;
  // slot_index: bisher fuer picture_<n> verwendet (mittlerweile entfernt). Felder ohne
  // slot-spezifische Semantik bekommen null — die DB-Spalte ist NULLable.
  const slotIdx: number | null = null;

  // Letzte Dropdown-Zeile ist keine Quelle, sondern eine Aktion. Die Auswahl wird auf den
  // bisherigen Wert zurückgestellt, BEVOR der Dialog aufgeht — bricht der Nutzer ab, steht
  // im Dropdown sonst dauerhaft „Zusatzfeld anlegen …" ohne dass etwas gemappt wäre.
  if (newSourceKey === CREATE_EAV_VALUE) {
    const sel = host.querySelector<HTMLSelectElement>(`select[data-field-source="${cssEscape(fieldKey)}"]`);
    if (sel) sel.value = sourceSelectValue(state.mappings.get(fieldKey) ?? {
      kaufland_field_key: fieldKey, field_group: fg, source_type: 'unmapped',
      vario_path: null, fixed_value: null, template: null, fallback: null,
      vario_resource_id: null, slot_index: slotIdx,
    });
    void openCreateListingFieldDialog(host, ctx, listing, state, displayField);
    return;
  }

  let mapping: ExistingFieldMapping;
  if (newSourceKey === CLEAR_VALUE) {
    // Auftrag an Kaufland, keine Quelle: Der Push sendet fuer dieses Attribut ein leeres
    // Array und entfernt es damit drueben. Gemessen am 2026-08-20; nur bei optionalen
    // Kategorie-Attributen anwaehlbar (siehe darfGeloeschtWerden).
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'clear', vario_path: null, fixed_value: null, template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else if (newSourceKey === '__unmapped__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'unmapped', vario_path: null, fixed_value: null, template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else if (newSourceKey === '__fixed_value__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'fixed_value', vario_path: null, fixed_value: '', template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else if (newSourceKey === '__template__') {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'template', vario_path: null, fixed_value: null, template: '', fallback: null, vario_resource_id: null, slot_index: slotIdx };
  } else {
    mapping = { kaufland_field_key: fieldKey, field_group: fg, source_type: 'vario_path', vario_path: newSourceKey, fixed_value: null, template: null, fallback: null, vario_resource_id: null, slot_index: slotIdx };
  }
  state.mappings.set(fieldKey, mapping);
  state.dirty = true;
  rerenderRow(host, ctx, listing, state, fieldKey);
  updateHeaderProgress(host, state);
  updateSaveButton(host, state);
}

function onValueChange(host: HTMLElement, state: EditorState, fieldKey: string, newValue: string): void {
  const m = state.mappings.get(fieldKey);
  if (!m) return;
  if (m.source_type === 'fixed_value') m.fixed_value = newValue;
  else if (m.source_type === 'template') m.template = newValue;
  else return;
  state.dirty = true;
  // Status-Stripe + Header-Progress aktualisieren ohne Input-Refocus zu zerstoeren.
  // Bei select-elementen (enum-feldern) ist das gewollt; bei freien text-inputs/textarea
  // wuerde ein re-render den focus zerstoeren, daher hier nur das dataset.status mutieren.
  const row = host.querySelector<HTMLElement>(`.kl-map-field[data-field-key="${cssEscape(fieldKey)}"]`);
  if (row) row.dataset['status'] = fieldStatus(state.displayFields.find((f) => f.key === fieldKey)!, m);
  updateHeaderProgress(host, state);
  updateSaveButton(host, state);
}

/** Listing-Override (Migration 0129) sofort speichern — eigene RPC, unabhaengig vom
 *  Profil-Save-Button. Leerer Wert loescht den Override (Feld erbt wieder das Profil).
 *  Zahlenfelder werden als jsonb-Number gespeichert, sonst als String. */
async function saveOverride(
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
  fieldKey: string,
  raw: string,
  el: HTMLInputElement | HTMLSelectElement,
): Promise<void> {
  const f = state.displayFields.find((x) => x.key === fieldKey);
  const value = parseOverrideValue(raw, f?.dataType);
  const { error } = await ctx.supabase.rpc('kaufland_set_listing_field_override', {
    _storefront: listing.storefront,
    _vario_article_id: listing.vario_article_id,
    _kaufland_field_key: fieldKey,
    _value: value,
  });
  if (error) {
    showToast(`Override speichern fehlgeschlagen: ${error.message}`, 'error');
    return;
  }
  if (value === null) state.overrides.delete(fieldKey);
  else state.overrides.set(fieldKey, value);
  // Visuelles Feedback: gesetzter Override = orangener Rahmen + hervorgehobene Caption.
  const set = value !== null;
  el.classList.toggle('is-set', set);
  const cap = el.parentElement?.querySelector('.kl-map-field-override-cap');
  if (cap) {
    cap.classList.toggle('is-set', set);
    cap.textContent = set ? '✓ nur dieser Artikel' : 'nur dieser Artikel';
  }
  showToast(set ? 'Override gespeichert (nur dieser Artikel)' : 'Override entfernt - erbt wieder das Profil', 'success');
}

/**
 * Legt ein Zusatzfeld am Verkaufskanal-Listing in Vario an und mappt das Kaufland-Feld direkt
 * darauf. Der Weg über Vario ist dem Direktwert vorzuziehen: Ein Direktwert gilt pro Artikel und
 * muss überall neu gepflegt werden, ein Zusatzfeld lässt sich in Vario einmal je Listing füllen.
 *
 * Der Feldtyp ist aus dem Kaufland-Feldtyp vorbelegt (`suggestEavType`) und änderbar.
 *
 * Nach dem Anlegen wird die neue Quelle IN DEN STATE eingefügt, statt den Editor neu zu laden —
 * ein Reload würde ungespeicherte Mappings verwerfen.
 */
async function openCreateListingFieldDialog(
  host: HTMLElement,
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
  field: DisplayField,
): Promise<void> {
  const vorschlag = suggestEavType(field.dataType);
  const typeOpts = EAV_TYPE_LABELS
    .map((t) => `<option value="${t.value}"${t.value === vorschlag ? ' selected' : ''}>${escapeHtml(t.label)}</option>`)
    .join('');

  const modal = openModal({
    title: 'Zusatzfeld in Vario anlegen',
    icon: 'fa-plus',
    width: 520,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(field.label)}</strong>
        <div class="text-grey">Kaufland-Feld <code>${escapeHtml(field.key)}</code> · Typ ${escapeHtml(field.dataType)}</div>
      </div>
      <div class="kl-form-row">
        <label for="kl-cef-label">Feldname in Vario</label>
        <div class="v-input"><input id="kl-cef-label" maxlength="100" value="${escapeHtml(field.label)}" /></div>
        <div class="kl-form-hint">So heißt das Feld später am Verkaufskanal-Listing.</div>
      </div>
      <div class="kl-form-row">
        <label for="kl-cef-type">Feldtyp</label>
        <div class="v-select"><select id="kl-cef-type">${typeOpts}</select></div>
        <div class="kl-form-hint">Vorbelegt anhand des Kaufland-Feldtyps. Langtext-Felder brauchen „Langtext", sonst zeigt Vario nur eine Zeile.</div>
      </div>
      <div class="kl-form-row">
        <label for="kl-cef-desc">Beschreibung (optional)</label>
        <div class="v-input"><input id="kl-cef-desc" maxlength="200" placeholder="Wofür ist das Feld?" /></div>
      </div>
      <div class="v-banner v-banner-info">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Das Feld landet in der Vario-Gruppe „Kaufland-Listing" und steht danach für alle Listings zur Verfügung. Werte pflegst du in Vario am Verkaufskanal des Artikels.</div>
      </div>
      <div class="kl-modal-error" id="kl-cef-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button-primary" id="kl-cef-go">Anlegen</button>
    `,
  });

  const labelInp = modal.el.querySelector<HTMLInputElement>('#kl-cef-label')!;
  const typeSel = modal.el.querySelector<HTMLSelectElement>('#kl-cef-type')!;
  const descInp = modal.el.querySelector<HTMLInputElement>('#kl-cef-desc')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#kl-cef-error')!;
  const goBtn = modal.el.querySelector<HTMLButtonElement>('#kl-cef-go')!;
  labelInp.focus();
  labelInp.select();

  goBtn.addEventListener('click', async () => {
    const label = labelInp.value.trim();
    if (label.length === 0) {
      errEl.textContent = 'Bitte einen Feldnamen angeben.';
      errEl.hidden = false;
      return;
    }
    errEl.hidden = true;
    goBtn.disabled = true;
    goBtn.textContent = 'Lege an …';
    try {
      const resp = await callFn<{
        ok: boolean; created: boolean; attributeId: string | null; label: string; type: string | null; reason?: string;
      }>(ctx, 'vario-create-listing-field', { label, type: typeSel.value, description: descInp.value.trim() });
      if (!resp.ok) throw new Error(resp.error);
      const { attributeId, created, reason } = resp.value;

      if (!attributeId) {
        // Vario hat angelegt, liefert die Id aber nicht zurück. Dann fehlt uns der `eav.<id>`-Key
        // für das Mapping — ehrlich sagen statt einen Key zu erfinden.
        modal.close();
        showToast('Feld in Vario angelegt. Bitte den Editor neu laden, damit es in der Quellenliste erscheint.', 'info');
        return;
      }

      const sourceKey = `eav.${attributeId}`;
      const neueQuelle: SourceDef = {
        sourceKey,
        path: `eav[${label}]`,
        dataType: field.dataType,
        label: `EAV · ${label}`,
      };
      // In die EAV-Gruppe der Quellenliste einsortieren; fehlt sie (kein einziges Zusatzfeld
      // bisher), wird sie angelegt.
      const eavGroup = state.sources.groups.find((g) => g.key === 'eav');
      if (eavGroup) eavGroup.sources = [...eavGroup.sources, neueQuelle].sort((a, b) => a.label.localeCompare(b.label, 'de'));
      else state.sources.groups.push({ key: 'eav', label: 'Freie Felder / EAV', sources: [neueQuelle] });

      state.mappings.set(field.key, {
        kaufland_field_key: field.key,
        field_group: field.originalFieldGroup,
        source_type: 'vario_path',
        vario_path: sourceKey,
        fixed_value: null,
        template: null,
        fallback: null,
        vario_resource_id: null,
        slot_index: null,
      });
      state.dirty = true;
      modal.close();
      rerenderRow(host, ctx, listing, state, field.key);
      updateHeaderProgress(host, state);
      updateSaveButton(host, state);
      showToast(
        created
          ? `Feld „${label}" in Vario angelegt und zugeordnet. Zum Übernehmen noch speichern.`
          : `Feld „${label}" gab es schon (${reason ?? 'vorhanden'}) — jetzt zugeordnet.`,
        'success',
      );
    } catch (e) {
      errEl.textContent = `Anlegen fehlgeschlagen: ${(e as Error).message}`;
      errEl.hidden = false;
      goBtn.disabled = false;
      goBtn.textContent = 'Anlegen';
    }
  });
}

function rerenderRow(host: HTMLElement, ctx: MappingTabCtx, listing: MappingTabListing, state: EditorState, fieldKey: string): void {
  const oldRow = host.querySelector<HTMLElement>(`.kl-map-field[data-field-key="${cssEscape(fieldKey)}"]`);
  if (!oldRow) return;
  const displayField = state.displayFields.find((f) => f.key === fieldKey);
  if (!displayField) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderFieldRow(displayField, {
    sources: state.sources,
    mapping: state.mappings.get(fieldKey),
    override: state.overrides.get(fieldKey),
  }, { withCreateEav: true });
  const newRow = tmp.firstElementChild as HTMLElement | null;
  if (!newRow) return;
  oldRow.replaceWith(newRow);
  // Events neu binden — nur fuer diese eine row.
  newRow.querySelectorAll<HTMLSelectElement>('select[data-field-source]').forEach((sel) => {
    sel.addEventListener('change', () => onSourceChange(host, ctx, listing, state, sel.dataset['fieldSource']!, sel.value));
  });
  newRow.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field-value]').forEach((inp) => {
    const handler = () => onValueChange(host, state, inp.dataset['fieldValue']!, (inp as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value);
    inp.addEventListener('input', handler);
    inp.addEventListener('change', handler);
  });
}

function updateHeaderProgress(host: HTMLElement, state: EditorState): void {
  const mapped = countMapped(state);
  const reqOpen = countRequiredOpen(state);
  const total = state.displayFields.length;

  // Header-Progress-Row: Bar + Text — wenn Container existiert, neu rendern.
  const progressRow = host.querySelector<HTMLElement>('.kl-map-progress-row');
  if (progressRow) {
    const okPct = total > 0 ? (mapped / total) * 100 : 0;
    const warnPct = total > 0 ? (reqOpen / total) * 100 : 0;
    progressRow.innerHTML = `
      <div class="kl-map-progress-bar" role="progressbar" aria-valuenow="${mapped}" aria-valuemin="0" aria-valuemax="${total}">
        <div class="kl-map-progress-seg-ok" style="width: ${okPct}%;"></div>
        <div class="kl-map-progress-seg-warn" style="width: ${warnPct}%;"></div>
      </div>
      <div class="kl-map-progress-text">${mapped} / ${total} Felder gemappt${reqOpen > 0 ? ` · ${reqOpen} Pflicht offen` : ''}</div>
    `;
  }

  // Pro Section: Mini-Progress + Count
  // Aufteilung MUSS spiegeln, was renderEditor() macht — sonst zaehlt das Vario-
  // Gruppen-Mini-Progress falsch. Vario-Felder werden vor Pflicht/Optional extrahiert.
  const varioFields = state.displayFields.filter((f) => isVarioField(f));
  const remainingFields = state.displayFields.filter((f) => !isVarioField(f));
  const requiredFields = remainingFields.filter((f) => f.required);
  const optionalFields = remainingFields.filter((f) => !f.required);
  host.querySelectorAll<HTMLElement>('.kl-map-group').forEach((groupEl) => {
    const groupKey = groupEl.dataset['groupKey'];
    if (!groupKey) return;
    const fields = groupKey === 'required' ? requiredFields
      : groupKey === 'vario' ? varioFields
      : groupKey === 'optional' ? optionalFields
      : [];
    const groupMeta = groupEl.querySelector<HTMLElement>('.kl-map-group-meta');
    if (!groupMeta) return;
    const groupMapped = fields.filter((f) => isMapped(state.mappings.get(f.key))).length;
    const groupReqOpen = fields.filter((f) => f.required && !isMapped(state.mappings.get(f.key))).length;
    groupMeta.innerHTML = `
      ${renderMiniProgress(groupMapped, fields.length, groupReqOpen)}
      <span class="kl-map-group-mini-count">${groupMapped} / ${fields.length}</span>
    `;
  });
}

function updateSaveButton(host: HTMLElement, state: EditorState): void {
  for (const id of ['#kl-map-save', '#kl-map-save-bottom']) {
    const btn = host.querySelector<HTMLButtonElement>(id);
    if (!btn) continue;
    btn.disabled = false;
    btn.classList.toggle('kl-map-save-dirty', state.dirty);
    btn.innerHTML = `<span class="v-icon fa-light fa-floppy-disk"></span>Speichern`;
  }
}

// ---------- Save ----------

/**
 * Schreibt die beiden Mindestpreis-Werte, sofern angefasst.
 *
 * Nur die dirty-markierten Werte gehen raus. Das ist nicht nur sparsam, es ist notwendig: ist die
 * Ladung fehlgeschlagen (fail-soft), stehen beide Felder leer da, und ein unbedingtes Schreiben
 * wuerde einen vorhandenen Mindestpreis stillschweigend loeschen.
 *
 * Rueckgabe: `null` bei Erfolg, sonst die Fehlermeldung. Das dirty-Flag bleibt bei einem Fehler
 * stehen, damit der naechste Speichern-Klick es erneut versucht.
 */
async function saveMinPrice(
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
): Promise<string | null> {
  if (state.minPrice.dirtyShared) {
    const { error } = await ctx.supabase.rpc('kaufland_set_optional_field_value', {
      _storefront: listing.storefront,
      _attribute_id: MIN_PRICE_ATTR,
      _value: state.minPrice.shared,
    });
    if (error) return error.message;
    state.minPrice.dirtyShared = false;
  }
  if (state.minPrice.dirtyArticle) {
    const { error } = await ctx.supabase.rpc('kaufland_set_optional_field_override', {
      _storefront: listing.storefront,
      _attribute_id: MIN_PRICE_ATTR,
      _vario_article_id: listing.vario_article_id,
      _value: state.minPrice.article,
    });
    if (error) return error.message;
    state.minPrice.dirtyArticle = false;
  }
  return null;
}

/**
 * Ist ein Festwert inhaltlich leer? Bewusst umfassender als ein Null-Vergleich -- ein
 * leeres Array oder Objekt ist bei Kaufland genauso wirkungslos wie ein leerer Text.
 *
 * `0` und `false` sind ECHTE Werte und bleiben es: die Zahl Null und der Wahrheitswert
 * falsch sind vollwertige Attributinhalte. Spiegelt `istLeererWert` im MCP
 * (supabase/functions/kaufland-mcp/execute.ts).
 */
export function istLeererFestwert(wert: unknown): boolean {
  if (wert === null || wert === undefined) return true;
  if (typeof wert === 'string') return wert.trim() === '';
  if (Array.isArray(wert)) return wert.length === 0;
  if (typeof wert === 'object') return Object.keys(wert as Record<string, unknown>).length === 0;
  return false;
}

async function saveProfile(
  host: HTMLElement,
  ctx: MappingTabCtx,
  listing: MappingTabListing,
  state: EditorState,
  opts: { silent?: boolean } = {},
): Promise<boolean> {
  const categoryId = listing.kaufland_category_id;
  if (!categoryId) return false;
  const storefront = listing.storefront;
  const locale = localeFor(storefront);

  // Save: eine Row pro display-key (DB-Constraint UNIQUE(profile_id, kaufland_field_key)).
  // Bei Multi-Target-Feldern (z.B. EAN -> product_data + unit) bedient der Push-Service
  // das fan-out automatisch via spezialroute in kaufland-listing-preview.
  const fieldsPayload = [];
  for (const df of state.displayFields) {
    const m = state.mappings.get(df.key);
    if (!m || m.source_type === 'unmapped') continue;
    // ZWEITER RIEGEL: ein Festwert OHNE Inhalt wird nicht geschrieben.
    //
    // Er waere keine Zuordnung, sondern ein LOESCHAUFTRAG an Kaufland (leeres Array,
    // packages/kaufland-product-data/src/index.ts:969) -- und zwar fuer JEDEN Artikel der
    // Kategorie ohne eigenen Wert. Die Vorbelegung, die solche Zeilen erzeugt hat, ist
    // entfallen (lib/mapping-defaults.ts); dieser Riegel faengt jeden anderen Weg ab,
    // etwa ein Feld, das der Haendler auf "Fester Wert" stellt und dann leer laesst.
    //
    // Der AUSDRUECKLICHE Loeschweg bleibt unberuehrt: dafuer gibt es
    // `source_type='clear'` ("Wert bei Kaufland loeschen", Migration 0054). Eine Absicht
    // braucht eine eigene Geste, kein leeres Textfeld.
    if (m.source_type === 'fixed_value' && istLeererFestwert(m.fixed_value)) continue;
    fieldsPayload.push({
      kaufland_field_key: df.key,
      field_group: df.originalFieldGroup,
      source_type: m.source_type,
      vario_path: m.vario_path,
      fixed_value: m.fixed_value,
      template: m.template,
      fallback: m.fallback,
      vario_resource_id: m.vario_resource_id ?? null,
      slot_index: m.slot_index ?? null,
    });
  }

  const saveBtns = host.querySelectorAll<HTMLButtonElement>('#kl-map-save, #kl-map-save-bottom');
  saveBtns.forEach((b) => {
    b.disabled = true;
    b.innerHTML = `<span class="v-icon fa-light fa-spinner-third fa-spin"></span>Speichere …`;
  });

  const { error } = await ctx.supabase.rpc('kaufland_save_mapping_profile', {
    _storefront: storefront,
    _locale: locale,
    _category_id: categoryId,
    _fields: fieldsPayload,
    _variants: [],
  });

  if (error) {
    showToast(`Speichern fehlgeschlagen: ${error.message}`, 'error');
    saveBtns.forEach((b) => {
      b.disabled = false;
      b.innerHTML = `<span class="v-icon fa-light fa-floppy-disk"></span>Speichern`;
    });
    return false;
  }

  // Mindestpreis nach dem Profil, mit eigenen RPCs. Schlaegt das fehl, ist das Profil trotzdem
  // gespeichert — deshalb eine eigene Meldung statt eines gemeinsamen "fehlgeschlagen", das den
  // Nutzer glauben liesse, auch die Zuordnungen seien verloren.
  const minErr = await saveMinPrice(ctx, listing, state);
  if (minErr) {
    showToast(`Zuordnungen gespeichert, Mindestpreis nicht: ${minErr}`, 'error');
    saveBtns.forEach((b) => {
      b.disabled = false;
      b.innerHTML = `<span class="v-icon fa-light fa-floppy-disk"></span>Speichern`;
    });
    return false;
  }

  if (!opts.silent) showToast(`Mapping gespeichert (${fieldsPayload.length} Felder).`, 'success');
  state.dirty = false;
  // Beim silent-save (z.B. via Vorschau-Button) nicht neu rendern — der Parent-Tab schaltet
  // gleich auf preview, ein zwischenzeitlicher mapping-rerender waere flackernd.
  if (!opts.silent) await renderMappingTab(host, ctx, listing);
  return true;
}
